// Orders service - API client for order operations
import type { OrderStatus, OrderPaymentStatus, FulfillmentType, DeliveryStatus, OrderFilters } from '@/types/orders';
import { apiFetch } from '@/lib/operator-api';

export interface OrderLineItem {
  id: string;
  name: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface Order {
  id: string;
  orderNumber: string;
  businessId: string;
  customerId?: string;
  customerName: string;
  customerPhone?: string;
  linkedAppointmentId?: string;
  status: OrderStatus;
  paymentStatus: OrderPaymentStatus;
  paymentId?: string;
  fulfillmentType: FulfillmentType;
  deliveryStatus?: DeliveryStatus;
  deliveryAddress?: string;
  deliveryNotes?: string;
  deliveryContactName?: string;
  deliveryContactPhone?: string;
  deliveryDispatchedAt?: Date;
  deliveryArrivedAt?: Date;
  deliveryCompletedAt?: Date;
  deliveryProofImageUrl?: string;
  lineItems: OrderLineItem[];
  total: number;
  paidAmount: number;
  createdAt: Date;
  updatedAt?: Date;
}

export interface OrderListResponse {
  orders: Order[];
  total: number;
}

export async function getOrders(filters: OrderFilters): Promise<OrderListResponse> {
  if (!filters.businessId) return { orders: [], total: 0 };

  const params = new URLSearchParams({ businessId: filters.businessId });
  if (filters.status && filters.status !== 'ALL') params.set('status', filters.status);
  if (filters.paymentStatus && filters.paymentStatus !== 'ALL') params.set('paymentStatus', filters.paymentStatus);
  if (filters.fulfillmentType && filters.fulfillmentType !== 'ALL') params.set('fulfillmentType', filters.fulfillmentType);

  const res = await apiFetch(`/api/operator/orders?${params.toString()}`);
  if (!res.ok) throw new Error(`Failed to fetch orders: ${res.status}`);

  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Failed to fetch orders');

  const orders: Order[] = (json.data || []).map((o: any) => ({
    id: o.id,
    orderNumber: o.orderNumber || o.id.slice(0, 8),
    businessId: o.businessId || o.business?.id || '',
    customerId: o.customerId || o.customer?.id,
    customerName: o.customerName || o.customer?.name || 'Customer',
    customerPhone: o.customer?.phoneNumberE164,
    status: o.status as OrderStatus,
    paymentStatus: (o.paymentStatus || 'UNPAID') as OrderPaymentStatus,
    fulfillmentType: (o.fulfillmentType || 'IN_STORE') as FulfillmentType,
    deliveryStatus: o.deliveryStatus as DeliveryStatus | undefined,
    deliveryAddress: o.deliveryAddress,
    deliveryNotes: o.deliveryNotes,
    deliveryContactName: o.deliveryContactName,
    deliveryContactPhone: o.deliveryContactPhone,
    deliveryDispatchedAt: o.deliveryDispatchedAt ? new Date(o.deliveryDispatchedAt) : undefined,
    deliveryArrivedAt: o.deliveryArrivedAt ? new Date(o.deliveryArrivedAt) : undefined,
    deliveryCompletedAt: o.deliveryCompletedAt ? new Date(o.deliveryCompletedAt) : undefined,
    deliveryProofImageUrl: o.deliveryProofImageUrl,
    lineItems: (o.lineItems || []).map((li: any) => ({
      id: li.id,
      name: li.productName || li.description || 'Item',
      quantity: Number(li.quantity),
      unitPrice: Number(li.unitPrice),
      total: Number(li.totalPrice),
    })),
    total: Number(o.total || 0),
    paidAmount: Number(o.paidAmount || 0),
    createdAt: new Date(o.createdAt),
    updatedAt: o.updatedAt ? new Date(o.updatedAt) : undefined,
  }));

  return { orders, total: json.pagination?.totalCount || orders.length };
}

export async function updateOrderStatus(
  orderId: string,
  status: OrderStatus
): Promise<{ success: boolean; error?: string }> {
  const res = await apiFetch(`/api/operator/orders/${orderId}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });

  const json = await res.json();
  return { success: json.success, error: json.error };
}

export interface CreateOrderLineItem {
  description?: string;
  unitPrice?: number;
  quantity: number;
  serviceId?: string;
  catalogProductId?: string;
}

export interface CreateOrderInput {
  businessId: string;
  customerId: string;
  customerName?: string;
  lineItems: CreateOrderLineItem[];
  notes?: string;
  fulfillmentType?: FulfillmentType;
  deliveryAddress?: string;
  deliveryNotes?: string;
  deliveryContactName?: string;
  deliveryContactPhone?: string;
}

export async function createOrder(
  input: CreateOrderInput
): Promise<{ success: boolean; data?: Order; error?: string }> {
  const res = await apiFetch('/api/operator/orders', {
    method: 'POST',
    body: JSON.stringify(input),
  });

  const json = await res.json();
  if (!json.success) return { success: false, error: json.error };

  const o = json.data;
  const order: Order = {
    id: o.id,
    orderNumber: o.orderNumber || o.id.slice(0, 8),
    businessId: o.businessId || o.business?.id || '',
    customerId: o.customerId || o.customer?.id,
    customerName: o.customerName || o.customer?.name || 'Customer',
    customerPhone: o.customer?.phoneNumberE164,
    status: o.status as OrderStatus,
    paymentStatus: (o.paymentStatus || 'UNPAID') as OrderPaymentStatus,
    fulfillmentType: (o.fulfillmentType || 'IN_STORE') as FulfillmentType,
    deliveryStatus: o.deliveryStatus as DeliveryStatus | undefined,
    deliveryAddress: o.deliveryAddress,
    deliveryNotes: o.deliveryNotes,
    deliveryContactName: o.deliveryContactName,
    deliveryContactPhone: o.deliveryContactPhone,
    deliveryDispatchedAt: o.deliveryDispatchedAt ? new Date(o.deliveryDispatchedAt) : undefined,
    deliveryArrivedAt: o.deliveryArrivedAt ? new Date(o.deliveryArrivedAt) : undefined,
    deliveryCompletedAt: o.deliveryCompletedAt ? new Date(o.deliveryCompletedAt) : undefined,
    deliveryProofImageUrl: o.deliveryProofImageUrl,
    lineItems: (o.lineItems || []).map((li: any) => ({
      id: li.id,
      name: li.productName || li.description || 'Item',
      quantity: Number(li.quantity),
      unitPrice: Number(li.unitPrice),
      total: Number(li.totalPrice),
    })),
    total: Number(o.total || 0),
    paidAmount: Number(o.paidAmount || 0),
    createdAt: new Date(o.createdAt),
    updatedAt: o.updatedAt ? new Date(o.updatedAt) : undefined,
  };

  return { success: true, data: order };
}

export interface CatalogProduct {
  id: string;
  name: string;
  sku: string;
  price: string;
  priceCents: number;
  availability: string;
  imageUrl?: string;
  category?: string;
}

export async function searchCatalogProducts(
  businessId: string,
  search: string,
  limit: number = 20
): Promise<CatalogProduct[]> {
  const params = new URLSearchParams({ businessId, limit: String(limit) });
  if (search) params.set('search', search);

  const res = await apiFetch(`/api/operator/catalog/products?${params.toString()}`);
  if (!res.ok) return [];

  const json = await res.json();
  if (!json.success) return [];

  return (json.data?.products || []).map((p: any) => ({
    id: p.id,
    name: p.name,
    sku: p.sku || p.localSku || '',
    price: p.price,
    priceCents: p.priceCents,
    availability: p.availability,
    imageUrl: p.imageUrl,
    category: p.category,
  }));
}

export async function updateDeliveryStatus(
  orderId: string,
  deliveryStatus: DeliveryStatus,
  deliveryProofImageUrl?: string
): Promise<{ success: boolean; error?: string }> {
  const res = await apiFetch(`/api/operator/orders/${orderId}/delivery-status`, {
    method: 'PATCH',
    body: JSON.stringify({ deliveryStatus, ...(deliveryProofImageUrl ? { deliveryProofImageUrl } : {}) }),
  });

  const json = await res.json();
  return { success: json.success, error: json.error };
}

export async function updateOrderPayment(
  orderId: string,
  status: OrderPaymentStatus,
  _amount: number,
  _method: string
): Promise<{ success: boolean; error?: string }> {
  const res = await apiFetch(`/api/operator/orders/${orderId}`, {
    method: 'PATCH',
    body: JSON.stringify({ paymentStatus: status }),
  });

  const json = await res.json();
  return { success: json.success, error: json.error };
}
